import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useThemeColors } from '../hooks/useThemeColors';
import type { RefinedSegment } from '../types';

interface Props {
  segments: RefinedSegment[];
  loading: boolean;
  onSeek: (sec: number) => void;
}

const fmt = (sec: number) =>
  `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;

export default function TranscriptTab({ segments, loading, onSeek }: Props) {
  const t = useThemeColors();
  const [showOriginal, setShowOriginal] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return segments;
    return segments.filter(
      (s) => (s.refined_text || '').includes(q) || (s.original_text || '').includes(q),
    );
  }, [segments, query]);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: t.muted }}>文字起こしを読み込み中...</Typography>
      </Box>
    );
  }
  if (segments.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
        <Typography sx={{ color: t.muted }}>
          補正済み文字起こしがまだ生成されていません。
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: t.faint }}>
          ファクト抽出処理と同時に生成されます。
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* 検索＋原文トグル */}
      <Box sx={{ display: 'flex', gap: 1.25, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="文字起こしを検索…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ fontSize: 18, color: t.faint, mr: 1 }} />,
          }}
          sx={{
            flex: 1,
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              borderRadius: 999,
              backgroundColor: t.paper,
              fontSize: 13,
            },
          }}
        />
        <Button
          size="small"
          onClick={() => setShowOriginal((v) => !v)}
          sx={{
            flexShrink: 0,
            color: t.muted,
            fontWeight: 700,
            border: `1px solid ${t.line}`,
            borderRadius: 999,
            px: 1.75,
          }}
        >
          {showOriginal ? '原文を隠す' : '原文を表示'}
        </Button>
      </Box>

      {/* パネル */}
      <Box
        sx={{
          border: `1px solid ${t.line}`,
          borderRadius: 2,
          backgroundColor: t.paper,
          backdropFilter: 'blur(8px)',
          px: 2,
        }}
      >
        {filtered.map((segment, i) => (
          <Box
            key={segment.segment_id}
            sx={{
              display: 'grid',
              gridTemplateColumns: '74px 1fr',
              gap: 1.5,
              py: 1.5,
              borderTop: i === 0 ? 'none' : `1px solid ${t.line}`,
            }}
          >
            <Chip
              label={`▶ ${fmt(segment.start_sec)}`}
              size="small"
              onClick={() => onSeek(segment.start_sec)}
              sx={{
                height: 'fit-content',
                width: 'fit-content',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 11.5,
                color: t.teal,
                backgroundColor: t.verdict.unverifiable.tint,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            <Box>
              <Typography sx={{ fontSize: 15, lineHeight: 1.8, color: t.ink }}>
                {segment.refined_text}
              </Typography>
              {showOriginal && segment.original_text !== segment.refined_text && (
                <Box sx={{ mt: 0.75, display: 'flex', gap: 0.875, alignItems: 'baseline' }}>
                  <Box
                    component="span"
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: t.muted,
                      border: `1px solid ${t.line}`,
                      borderRadius: 0.75,
                      px: 0.75,
                      flexShrink: 0,
                    }}
                  >
                    原文
                  </Box>
                  <Typography sx={{ fontSize: 12.5, color: t.faint, lineHeight: 1.6 }}>
                    {segment.original_text}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ))}
        {filtered.length === 0 && (
          <Typography sx={{ py: 2.5, color: t.faint, fontSize: 13 }}>
            「{query}」に一致する文字起こしはありません。
          </Typography>
        )}
      </Box>
    </Box>
  );
}
